import React, {useCallback, useState, useRef, useEffect} from 'react';

import {useSlate} from 'slate-react';
import {useShallow} from 'zustand/react/shallow';
import {
  useQuestionTranslationStore,
  fontDropdownText
} from 'client/components/QuestionTypes/shared/TranslationSection';
import {DropdownItem, Icon, Button} from '@albert-io/atomic';

import useOnClickOutside from 'lib/hooks/useOnClickOutside';

import type {FontType, ButtonProps} from '../../Utils/editor.utils';
import {areBlocksActive, toggleBlock, withWorkaround, fontTypes} from '../../Utils/editor.utils';

import {closeOnFocusOut} from './closeOnFocusOut';

type FontDropdownProps = Omit<ButtonProps<FontType>, 'icon' | 'type'>;

interface TrayCloseOptions {
  returnFocus?: boolean;
}

const maxIndex = 3;
export const FontDropdown = ({tabIndex, ...rest}: FontDropdownProps) => {
  const editor = useSlate();
  const isActive = areBlocksActive(editor, [...fontTypes]);
  const [showDropdown, setShowDropdown] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = useCallback(() => {
    if (showDropdown) {
      setShowDropdown(false);
    }
  }, [showDropdown]);

  useOnClickOutside(containerRef, handleClickOutside);

  const closeTray = ({returnFocus = false}: TrayCloseOptions = {}) => {
    setShowDropdown(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  return (
    <div
      ref={containerRef}
      onBlur={(event) => closeOnFocusOut(event, containerRef.current, () => setShowDropdown(false))}
    >
      <Button
        ref={triggerRef}
        aria-label='font dropdown button'
        className='written-submission__font-button'
        color={isActive ? 'primary' : 'secondary'}
        variant={isActive ? 'faded' : 'outlined'}
        onClick={() => {
          setShowDropdown((isShown) => !isShown);
        }}
        tabIndex={showDropdown ? -1 : tabIndex}
        {...rest}
      >
        <Icon icon='text-size' />
        <Icon icon='caret-down' />
      </Button>
      {showDropdown && <FontDropdown.Tray onClose={closeTray} />}
    </div>
  );
};
type FontToText = {
  [key in FontType]: {text: string; class: string};
};

const fontTypeToAttr: FontToText = {
  heading: {text: 'Title', class: 'written-submission__title'},
  'heading-one': {text: 'Heading', class: 'written-submission__heading'},
  'heading-two': {text: 'Subheading', class: 'written-submission__subheading'}
};

// A named component (rather than assigning the arrow function directly to
// FontDropdown.Tray) so eslint's rules-of-hooks recognizes it as a component.
const FontDropdownTray = ({onClose}: {onClose: (options?: TrayCloseOptions) => void}) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  const editor = useSlate();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeCursor, setActiveCursor] = useState(0);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveCursor((oldCursor) => (oldCursor < maxIndex ? oldCursor + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveCursor((oldCursor) => (oldCursor > 0 ? oldCursor - 1 : maxIndex));
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      onClose();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose({returnFocus: true});
    } else if (event.key === 'Enter') {
      event.preventDefault();
      (dropdownRef.current?.children[activeCursor] as HTMLElement | undefined)?.click();
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      const selected = dropdownRef.current.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.focus();
      }
    }
  }, [activeCursor]);

  return (
    <div
      className='written-submission__toolbar-dropdown rounded-x1 border-border-primary background-primary border border-solid'
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      {fontTypes.map((type, index) => {
        const isCurrent = activeCursor === index;
        return (
          <DropdownItem
            key={type}
            textProps={{className: fontTypeToAttr[type].class}}
            tabIndex={isCurrent ? 0 : -1}
            as='button'
            onClick={() => {
              withWorkaround(editor, () => {
                toggleBlock(editor, type);
                onClose();
              });
            }}
          >
            {showTranslations ? fontDropdownText[type] : fontTypeToAttr[type].text}
          </DropdownItem>
        );
      })}
      <DropdownItem
        as='button'
        tabIndex={activeCursor === maxIndex ? 0 : -1}
        onClick={() => {
          withWorkaround(editor, () => {
            toggleBlock(editor, 'paragraph');
            onClose();
          });
        }}
      >
        {showTranslations ? fontDropdownText.body : 'Body'}
      </DropdownItem>
    </div>
  );
};

FontDropdown.Tray = FontDropdownTray;
